import React from 'react';
import { Link } from 'react-router-dom';
import { BlogArticleLayout } from '../../components/blog/BlogArticleLayout';

const QUICK_ANSWER = "WallStreetBets (r/wallstreetbets) tends to focus on a recognisable rotating set of stocks: meme stocks (GameStop, AMC), high-volatility tech (Tesla, NVIDIA, Palantir), short-squeeze candidates, and whatever recent IPO is trending. The community favours options-friendly, news-heavy names with extreme price moves. The list shifts week to week, but the personality of the picks stays the same.";

const FAQS = [
  { q: 'What does "WSB" actually stand for?', a: 'WallStreetBets — a subreddit (r/wallstreetbets) of about 17 million members, founded in 2012, focused on high-risk, high-reward retail trading. The community style is satirical and irreverent.' },
  { q: 'Are WSB stocks a good investment?', a: 'They are the opposite of buy-and-hold. WSB favourites are extremely volatile and often driven by short-term sentiment. They can deliver huge gains and catastrophic losses, sometimes in the same week.' },
  { q: 'How do I find what WSB is talking about right now?', a: 'Browse r/wallstreetbets directly, or use trackers like Quiver Quantitative and ApeWisdom that count daily ticker mentions. Wallstreetle\'s WallStreetBets category puzzle also tracks the active list.' },
  { q: 'Why do WSB stocks move so much?', a: 'They\'re heavily traded with options, often short-squeezed, and amplified by viral social posts. A small piece of news can trigger massive coordinated buying or selling.' },
  { q: 'Can I learn investing through WSB stocks?', a: 'Yes, but mostly about risk and volatility. They\'re a poor introduction to fundamentals because the price often disconnects entirely from the underlying business.' },
];

export default function WsbStocksPage() {
  return (
    <BlogArticleLayout
      slug="wsb-stocks"
      title="What Stocks Does WallStreetBets Actually Trade?"
      description="WallStreetBets has a recognisable list of favourites. Here is what shows up on r/wallstreetbets, why, and how to track it."
      keywords="wsb stocks, wallstreetbets stocks, what stocks does wsb trade, meme stocks, GameStop AMC, wsb favorites, retail trading stocks"
      quickAnswer={QUICK_ANSWER}
      quickAnswerText={QUICK_ANSWER}
      faqs={FAQS}
      related={['stock-picking', 'investing-mistakes-beginners-make', 'free-stock-market-games']}
      ctaText="Test yourself on WSB stocks"
      ctaSubtext="Wallstreetle has a dedicated WallStreetBets category. Today's mystery stock is one r/wsb is currently obsessed with."
      ctaHref="/wallstreetbets"
      ctaLabel="Play the WSB Category"
    >
      <section>
        <h2>What is WallStreetBets?</h2>
        <p>
          WallStreetBets (often shortened to WSB) is a subreddit with around 17 million members, founded in 2012.
          It became globally famous in early 2021 when its members coordinated to push GameStop's stock from under $20 to over $400, squeezing institutional short sellers and triggering Congressional hearings.
        </p>
        <p>
          The culture is irreverent, options-heavy, and openly speculative. Posts are full of memes, "loss porn" screenshots, and the community's signature self-mockery.
        </p>
      </section>

      <section>
        <h2>What kinds of stocks does WSB favour?</h2>
        <p>
          A few archetypes show up over and over:
        </p>
        <ul>
          <li><strong>Meme stocks</strong> — GameStop (GME), AMC, BlackBerry, Bed Bath & Beyond. Heavily shorted small-caps with social momentum.</li>
          <li><strong>High-volatility tech</strong> — Tesla (TSLA), NVIDIA (NVDA), Palantir (PLTR), Coinbase (COIN). Big news, big options volume.</li>
          <li><strong>Recent IPOs and SPACs</strong> — anything new and dramatic.</li>
          <li><strong>Short-squeeze candidates</strong> — stocks with high short interest where a coordinated buy-in could force shorts to cover.</li>
          <li><strong>Crypto-adjacent stocks</strong> — MicroStrategy (MSTR), Marathon Digital (MARA), Riot.</li>
        </ul>
      </section>

      <section>
        <h2>Why these stocks?</h2>
        <p>
          Three structural reasons:
        </p>
        <ul>
          <li><strong>Options friendliness.</strong> WSB loves leverage. Stocks with deep, liquid options markets allow huge gains (and losses) on small price moves.</li>
          <li><strong>Volatility.</strong> Boring stocks don't generate posts. The community gravitates to anything that swings 5%+ a day.</li>
          <li><strong>Asymmetric narrative.</strong> A clear story (short squeeze, AI hype, retail vs hedge funds) drives coordinated attention.</li>
        </ul>
      </section>

      <section>
        <h2>How can you track WSB activity?</h2>
        <p>
          Useful trackers:
        </p>
        <ul>
          <li><strong>r/wallstreetbets daily discussion</strong> — the firehose, but unfiltered.</li>
          <li><strong>Quiver Quantitative</strong> — counts ticker mentions across WSB posts and comments.</li>
          <li><strong>ApeWisdom</strong> — similar, with sentiment scoring.</li>
          <li><strong><Link to="/wallstreetbets">Wallstreetle's WSB category</Link></strong> — daily puzzle drawn from currently trending tickers.</li>
        </ul>
      </section>

      <section>
        <h2>Should you actually trade WSB stocks?</h2>
        <p>
          A blunt answer: WSB favourites are entertainment as much as investment. Many are detached from fundamentals, and the people posting are often gambling.
          They can be profitable, especially during squeezes, but the realistic outcome distribution looks more like a casino than a portfolio.
        </p>
        <p>
          If you want to engage with WSB stocks productively, treat them as a study in market psychology. Pair the experience with our guide on <Link to="/blog/investing-mistakes-beginners-make">common beginner mistakes</Link> so you understand what you're up against.
        </p>
      </section>
    </BlogArticleLayout>
  );
}
